import { Injectable } from "@angular/core";
import { Beneficiary, BeneficiaryState, NewBeneficiary } from "@models/beneficiary.model";
import { Person } from "@services/zok";
import { findMatchingPerson } from "@weps/shared-validators";

/**
 * Manages DOB-based duplicate detection for beneficiaries.
 * Maintains an incremental Map<dateOfBirth, Beneficiary> for O(1) lookups.
 */
@Injectable({
  providedIn: "root",
})
export class BeneficiaryDuplicateDetectionService {
  private dobMap = new Map<string, Beneficiary>();

  /** Full rebuild — only called once on init when restoring persisted state. */
  rebuildFromState(state: BeneficiaryState): void {
    this.dobMap.clear();
    for (const bene of state.primaryBeneficiaries) {
      this.add(bene);
    }
    for (const bene of state.backupBeneficiaries) {
      this.add(bene);
    }
  }

  add(bene: Beneficiary): void {
    const dob = bene.kind === "new" ? bene.personData?.dateOfBirth : null;
    if (dob) this.dobMap.set(dob, bene);
  }

  remove(bene: Beneficiary): void {
    const dob = bene.kind === "new" ? bene.personData?.dateOfBirth : null;
    if (dob) this.dobMap.delete(dob);
  }

  findByDob(bene: Beneficiary): Beneficiary | null {
    if (bene.kind !== "new" || !bene.personData) return null;
    return this.dobMap.get(bene.personData.dateOfBirth) ?? null;
  }

  /**
   * Checks if a new beneficiary matches an existing household member by name + DOB.
   * Uses the shared duplicate detection utility.
   */
  findDuplicateInHousehold(bene: NewBeneficiary, householdPeople: Person[]): Person | null {
    if (!bene.personData) return null;
    return findMatchingPerson(bene.personData, householdPeople);
  }

  /** Check if a beneficiary with the same householdMemberId already exists in a section. */
  isDuplicateInSection(bene: Beneficiary, section: Beneficiary[]): boolean {
    if (bene.kind === "new") return false; // New beneficiaries (householdMemberId: null) can't be duplicates by ID
    return section.some(
      (b) => b.kind === "existing" && b.householdMemberId === bene.householdMemberId,
    );
  }

  clear(): void {
    this.dobMap.clear();
  }
}
